import React, { FC, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers'
import { ISelectOption } from 'components/forms/Selects'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { CarrierSelect } from 'apps/fleet-planner/components/atoms'
import {
  AddEntityForm,
  AddEntityFormFooter,
} from 'apps/fleet-planner/components/forms/AddEntityForm'
import { IAddNewEntityFormProps } from 'apps/fleet-planner/components/forms/TrailerForm'
import { TruckSchema, TruckSchemaType } from 'apps/fleet-planner/schema/TruckSchema'
import { ICarrier } from 'apps/fleet-planner/types'
import { selectCarrierOptionList, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { Button } from 'transfix-ui'

export interface ITruckFormProps extends IAddNewEntityFormProps<TruckSchemaType> {}

export const TruckForm: FC<ITruckFormProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = {
    truckNumber: undefined,
    motorCarrier: undefined,
    motorCarrierId: undefined,
  },
  isLoading: isLoadingProp,
  primaryButtonText: primaryButtonTextProp = 'Add Truck',
  disabledFields = [],
}) => {
  const carriersOptions = useSelector(selectCarrierOptionList)
  const carriers = useSelector(selectCarriersList)

  const getDefaultMotorCarrier = (): ICarrier | undefined => {
    let carrier: ICarrier | undefined

    if (defaultValuesProp?.motorCarrierId) {
      carrier = carriers.find(c => c.id === defaultValuesProp.motorCarrierId)
    } else if (carriers?.length === 1) {
      const [firstCarrier] = carriers || []
      carrier = firstCarrier
    }

    return carrier
  }

  const motorCarrier = getDefaultMotorCarrier()
  const { motorCarrierId } = motorCarrier || {}

  const { control, errors, handleSubmit, setValue } = useForm<TruckSchemaType>({
    mode: 'onSubmit',
    resolver: yupResolver(TruckSchema),
    defaultValues: TruckSchema.cast({
      ...defaultValuesProp,
      motorCarrier,
      motorCarrierId,
    }),
  })

  /**
   * Update if this value is set async for some reason
   */
  useEffect(() => {
    const matchedCarrier = carriers.find(c => c.id === defaultValuesProp?.motorCarrierId)
    if (matchedCarrier) {
      setValue('motorCarrierId', matchedCarrier?.id)
      setValue('motorCarrier', matchedCarrier)
    }
  }, [carriers, defaultValuesProp.motorCarrierId, setValue])

  const submitHandler = (data: TruckSchemaType) => {
    onSuccessProp(data)
  }

  return (
    <AddEntityForm dataTest='add-new-truck-form'>
      {/* Motor Carrier ID */}
      <ControlledHiddenInput
        control={control}
        name='motorCarrierId'
        defaultValue={defaultValuesProp.motorCarrierId}
      />
      {/* Trailer Number */}
      <div className='mb4'>
        <DeprecatedControlledTextInput
          control={control}
          name='truckNumber'
          defaultValue={defaultValuesProp?.truckNumber}
          TextInputProps={{
            label: 'Truck Number or Nickname',
            id: 'truckNumber',
            name: 'truckNumber',
            large: true,
            autoFocus: true,
            required: true,
            validationMessage: errors?.truckNumber?.message,
            invalid: !!errors?.truckNumber?.message,
            placeholder: 'Enter truck #',
            dataTest: 'truck-number-input',
          }}
        />
      </div>
      {/* Employer */}
      <div className='mb5'>
        <Controller
          name='motorCarrier'
          control={control}
          render={({ onChange, value }) => {
            const changeHandler = ({ value: mcId }: ISelectOption<number>) => {
              const matchingCarrier = carriers.find(c => c.id === mcId)
              if (matchingCarrier) {
                onChange({
                  id: matchingCarrier?.id,
                  name: matchingCarrier.name,
                })
                setValue('motorCarrierId', matchingCarrier.id)
              }
            }

            return (
              <CarrierSelect
                large
                label='Carrier'
                required
                fetchList={!carriersOptions?.length}
                motorCarrierId={value?.id}
                onChange={changeHandler}
                validationMessage={errors?.motorCarrierId?.message}
                invalid={!!errors?.motorCarrierId?.message}
                disabled={disabledFields.includes('motorCarrierId')}
              />
            )
          }}
        />
      </div>
      <AddEntityFormFooter>
        <Button color='tertiary' size='large' onClick={onCancelProp} data-test='cancel-new-truck'>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={isLoadingProp}
          size='large'
          type='submit'
          data-test='save-new-truck'
          onClick={handleSubmit(submitHandler)}>
          {primaryButtonTextProp}
        </Button>
      </AddEntityFormFooter>
    </AddEntityForm>
  )
}
